/**
 * 水平听辨测试
 * ============================================================
 * 对应需求文档 V1.0 的 A08：原本写的是「听辨、词汇、发音或跳过」。
 *
 * ── 为什么选「听辨」而不是词汇/发音 ──────────────────────────
 *   · 词汇：本质是选择题，能靠猜，区分度低
 *   · 发音：要用户开口，新用户第一步就要求发音会劝退，
 *           而且我们的声调分析准确率约 33%，用它评水平不可靠
 *   · 听辨：播一句方言，让用户选普通话意思 —— 不需要开口、
 *           能靠现有课程内容自动出题、且真正反映"能不能听懂"
 *
 * 出题完全基于**已有课程内容**，不额外维护题库：
 *   正确答案 = 某句的普通话释义
 *   干扰项   = 其它句子的释义（优先跨等级，见下）
 *
 * ── 本文件在 2026-10 做过一次加固，修掉三个问题 ──────────────
 *
 * 1. **题目太少（原 5 题）**
 *    5 题里错 2 题就是 40%，定档完全被单题运气左右。
 *    → 提到 12 题，并把题量作为可调参数。
 *
 * 2. **干扰项可能来自同等级，可以靠排除法猜对**
 *    同一课程组的句子释义高度相似（茶餐厅的「唔該埋單」/「唔該兩位」
 *    都跟"点餐/服务"有关），用户不需要听懂就能选出来。
 *    → 干扰项**优先从其它等级抽**，同等级仅在数量不足时兜底。
 *
 * 3. **只能测到中级的题，高级内容从不出现**
 *    调用方原来写死 `lessons.slice(0, 6)`，而课程列表按
 *    初级(5)→中级(5)→高级(5) 排列，所以切片只覆盖到
 *    「5 节初级 + 1 节中级」，**高级句子一句都测不到**，
 *    高级档因此永远不可达。
 *    → 新增 selectLessonSample()，按等级均衡抽样给调用方去取详情。
 */

/** 题目数量 */
export const TEST_SIZE = 12;

/**
 * 各等级的抽题配额（总数 = TEST_SIZE 时）
 *
 * 初级多抽：用户更可能落在初级，这一步的区分度最要紧。
 * 高级少抽但**必须 > 0** —— 高级内容一句不测的话，
 * 再高的分也证明不了"能上高级"，那样高级档就成了摆设。
 */
export const LEVEL_QUOTA = { beginner: 6, intermediate: 4, advanced: 2 };

/** 课程等级元信息（与后端 config/curriculum.js 的 LEVELS 保持一致） */
const STAGE_META = {
  beginner: { name: '初级', desc: '零基础，学会开口与最基本的生存表达' },
  intermediate: { name: '中级', desc: '能进行日常对话，开始掌握地道表达' },
  advanced: { name: '高级', desc: '应对复杂场景，说得像香港人' },
};

/** 等级先后顺序（路径建议按此排列） */
const STAGE_ORDER = ['beginner', 'intermediate', 'advanced'];

/** 去标点与空白，便于比较释义是否相同 */
function normText(s) {
  return String(s || '')
    .replace(/[，。！？、；：,.!?;:\s'"（）()「」【】]/g, '')
    .toLowerCase();
}

/** Fisher-Yates 洗牌（不改原数组） */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * 按等级均衡抽样要取详情的课程
 *
 * 调用方（OnboardingPage）拿到的是课程**列表**（只含 samplePhrase），
 * 需要逐课取详情才有完整 phrases。原实现直接 `slice(0,6)`，
 * 由于列表按等级排序，实际只取到了初级的课 —— 高级内容永远测不到。
 *
 * 本函数按等级交错抽样，保证抽出的课程覆盖各等级。
 *
 * ── 为什么优先用内置课（重要）────────────────────────────────
 * 用户生成的课程会混进列表，但**不适合做测评题**：
 *   · 实测 33 节生成课全都是 beginner 级，会把初级池撑到 38:5
 *     （生成:内置），随机抽几乎全抽到生成课
 *   · 生成课的 jyutping 实测为空字符串，题目质量不可控
 *   · 生成内容随用户变化，不同人的测评基准会不一致
 * 内置课是人工校对的（有 tests/curriculum.test.js 把关粤拼），
 * 所以**内置优先**，内置不够时（某等级课太少）才用生成课兜底。
 *
 * @param {Array} lessons - 课程列表项（需含 id、level，可选 generated）
 * @param {number} [perLevel=4] - 每个等级最多取几节
 * @returns {Array} 课程列表项的子集，按等级交错排列
 */
export function selectLessonSample(lessons = [], perLevel = 4) {
  const byLevel = {};
  for (const l of lessons) {
    const lv = l.level || 'beginner';
    (byLevel[lv] ||= []).push(l);
  }

  const picked = STAGE_ORDER.map((lv) => {
    const all = byLevel[lv] || [];
    // 内置课优先；generated 为真/或是生成课的排后面
    const builtIn = all.filter((l) => !l.generated);
    const generated = all.filter((l) => l.generated);

    const out = shuffle(builtIn).slice(0, perLevel);
    if (out.length < perLevel) {
      out.push(...shuffle(generated).slice(0, perLevel - out.length));
    }
    return out;
  });

  // 交错展开：初级、中级、高级、初级、中级…… 保证前缀也覆盖各等级
  const out = [];
  const maxLen = Math.max(0, ...picked.map((p) => p.length));
  for (let i = 0; i < maxLen; i++) {
    for (const group of picked) {
      if (group[i]) out.push(group[i]);
    }
  }
  return out;
}

/**
 * 按配额把 n 道题分配到各等级
 *
 * 若某等级的句子不够，把缺口让给其它等级，而不是少出题。
 *
 * @param {number} n
 * @param {Object} avail - { beginner: 7, intermediate: 6, advanced: 3 } 各等级可用句数
 * @returns {Object} 各等级实际分配数
 */
function allocateQuota(n, avail) {
  const totalQuota = Object.values(LEVEL_QUOTA).reduce((a, b) => a + b, 0);
  const want = {};
  for (const lv of STAGE_ORDER) {
    const ideal = Math.round((LEVEL_QUOTA[lv] / totalQuota) * n);
    want[lv] = Math.min(ideal, avail[lv] || 0);
  }

  // 补足：把没分完的额度按剩余容量发给还有句子的等级
  let assigned = Object.values(want).reduce((a, b) => a + b, 0);
  let guard = 0;
  while (assigned < n && guard++ < 100) {
    let progressed = false;
    for (const lv of STAGE_ORDER) {
      if (assigned >= n) break;
      if (want[lv] < (avail[lv] || 0)) {
        want[lv] += 1;
        assigned += 1;
        progressed = true;
      }
    }
    if (!progressed) break; // 全部等级都取空了
  }
  return want;
}

/**
 * 从课程里出题
 *
 * @param {Array} lessons - 课程数组（含 level 与 phrases[{cantonese, mandarin}]）
 * @param {number} [n] - 题目数量
 * @returns {Array<{
 *   id:string, cantonese:string, jyutping?:string, level:string,
 *   lessonId?:string, lessonTitle:string, options:string[], answerIndex:number
 * }>}
 */
export function buildQuestions(lessons = [], n = TEST_SIZE) {
  // 收集所有可用句子，保留 level 供后续按等级分配
  const pool = [];
  for (const l of lessons) {
    for (const p of l.phrases || []) {
      const m = String(p.mandarin || '').trim();
      const c = String(p.cantonese || '').trim();
      if (!m || !c) continue;
      pool.push({
        cantonese: c,
        mandarin: m,
        jyutping: p.jyutping,
        level: l.level || 'beginner',
        lessonId: l.id,
        lessonTitle: l.title,
      });
    }
  }
  if (pool.length < 3) return []; // 不足以出题（至少要有 1 正确 + 2 干扰）

  const byLevel = {};
  for (const item of pool) {
    (byLevel[item.level] ||= []).push(item);
  }
  const avail = Object.fromEntries(STAGE_ORDER.map((lv) => [lv, (byLevel[lv] || []).length]));
  const quota = allocateQuota(n, avail);

  // 按等级配额抽题：等级内先打散，避免题序与课程顺序一致
  let picked = [];
  for (const lv of STAGE_ORDER) {
    picked = picked.concat(shuffle(byLevel[lv] || []).slice(0, quota[lv]));
  }
  picked = shuffle(picked).slice(0, n);

  return picked.map((item, idx) => {
    const itemKey = normText(item.mandarin);

    // 干扰项优先取自**其它等级** —— 同等级句子语义相近，
    // 用户靠"哪句像这个场景"就能猜对，测不出真实听力水平。
    const crossLevel = shuffle(pool.filter((p) => p.level !== item.level));
    const sameLevel = shuffle(pool.filter((p) => p.level === item.level));

    const options = [item.mandarin];
    const seen = new Set([itemKey]);

    for (const cand of [...crossLevel, ...sameLevel]) {
      if (options.length >= 4) break;
      const key = normText(cand.mandarin);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      options.push(cand.mandarin);
    }

    // 若句子实在太少取不满 4 项，保留 3 项而非硬凑（避免重复选项）
    const rotated = shuffle(options);

    return {
      id: `q${idx + 1}`,
      cantonese: item.cantonese,
      jyutping: item.jyutping || '',
      level: item.level,
      lessonId: item.lessonId,
      lessonTitle: item.lessonTitle,
      options: rotated,
      answerIndex: rotated.indexOf(item.mandarin),
    };
  });
}

/**
 * 把答对数量映射成水平
 *
 * 分档理由：听辨测试只能区分"听懂多少"，不能细分到四档。
 * 基础三档且**宁低勿高** —— 高估用户水平会让第一课太难而劝退。
 *
 * ⚠️ 关于第 4 档（高级）：
 *   onboarding.js 的 LEVELS 里有 `good`→advanced，但原实现在这里
 *   永远只返回 zero/some/basic，导致**高级档只能靠用户自评进入**，
 *   测试结果永远推不到高级。
 *   现在题库覆盖了高级句子（见文件头第 3 条），所以有条件放行：
 *   必须**答对率 ≥ 0.9 且高级题至少对一半**才给 advanced。
 *   条件刻意设得很紧 —— 宁可让能上高级的人留在中级多上一节，
 *   也不要让听不懂的人被扔进高级课。
 *
 * 第 3 个参数 `byLevel` 是可选的：不传时行为与原实现**完全一致**
 * （永不返回 good），保证既有调用点不受影响。
 *
 * @param {number} correct
 * @param {number} total
 * @param {Object} [byLevel] - { beginner:{total,correct}, ... }
 * @returns {{levelId:string, label:string, desc:string, courseLevel:string}}
 */
export function scoreToLevel(correct, total, byLevel = null) {
  const rate = total > 0 ? correct / total : 0;
  const adv = byLevel?.advanced;
  const advRate = adv && adv.total > 0 ? adv.correct / adv.total : 0;

  // 高级：答对率很高，且高级题确实答得动
  if (rate >= 0.9 && adv && adv.total > 0 && advRate >= 0.5) {
    return {
      levelId: 'good',
      label: '比较熟练',
      desc: `答对 ${correct}/${total}（含高级题 ${adv.correct}/${adv.total}）—— 直接从高级课开始，提升地道程度`,
      courseLevel: 'advanced',
    };
  }

  if (rate >= 0.8) {
    return {
      levelId: 'basic',
      label: '能简单对话',
      desc: `答对 ${correct}/${total} —— 从中级课开始，跳过最基础的寒暄`,
      courseLevel: 'intermediate',
    };
  }
  if (rate >= 0.4) {
    return {
      levelId: 'some',
      label: '会一点点',
      desc: `答对 ${correct}/${total} —— 从初级课开始，但可以加快节奏`,
      courseLevel: 'beginner',
    };
  }
  return {
    levelId: 'zero',
    label: '零基础',
    desc: `答对 ${correct}/${total} —— 从初级第一课开始，慢慢来`,
    courseLevel: 'beginner',
  };
}

/**
 * 逐级统计
 *
 * @param {Array} answers - [{ level, correct }]
 * @returns {Object} { beginner:{total,correct,ratio}, ... }
 */
export function summarizeByLevel(answers = []) {
  const out = {};
  for (const lv of STAGE_ORDER) {
    const subset = answers.filter((a) => a.level === lv);
    if (!subset.length) continue;
    const correct = subset.filter((a) => a.correct).length;
    out[lv] = {
      total: subset.length,
      correct,
      ratio: Number((correct / subset.length).toFixed(3)),
    };
  }
  return out;
}

/**
 * 弱项分析
 *
 * 只在**样本 >= 2 题**的等级里找弱项：
 * 某等级只出 1 题且答错时，比例是 100% —— 拿它当"弱项"会误导用户。
 *
 * @param {Object} byLevel - summarizeByLevel 的结果
 * @returns {Array}
 */
export function findWeakSpots(byLevel = {}) {
  const candidates = Object.entries(byLevel)
    .filter(([, s]) => s.total >= 2)
    .map(([level, s]) => ({ level, ...s }))
    .sort((a, b) => a.ratio - b.ratio);

  const weakest = candidates[0];
  if (!weakest || weakest.ratio >= 0.7) return [];

  return [
    {
      level: weakest.level,
      levelName: STAGE_META[weakest.level]?.name || weakest.level,
      label: `${STAGE_META[weakest.level]?.name || weakest.level}段偏弱`,
      ratio: weakest.ratio,
      advice:
        weakest.ratio < 0.4
          ? '建议先回头把这一段的入门课刷一遍，再往下走。'
          : '这一段基本能应付，可以先跳过，遇到卡壳再回来补。',
    },
  ];
}

/**
 * 学习路径建议
 *
 * 从推荐起点往后的完整路线（不含起点之前的等级 —— 那些不用再上）。
 *
 * @param {string} startLevelId - beginner | intermediate | advanced
 * @returns {Array}
 */
export function buildPathAdvice(startLevelId = 'beginner') {
  const startIdx = Math.max(0, STAGE_ORDER.indexOf(startLevelId));
  return STAGE_ORDER.slice(startIdx).map((lv, i) => ({
    level: lv,
    levelName: STAGE_META[lv]?.name || lv,
    desc: STAGE_META[lv]?.desc || '',
    stage: i === 0 ? '从这里开始' : `第 ${i + 1} 阶段`,
    isStart: i === 0,
  }));
}

/**
 * 汇总测评结果
 *
 * 一站式：定档 + 逐级表现 + 弱项 + 路径建议。
 *
 * @param {Array} answers - [{ level, correct }]（按作答顺序）
 * @returns {Object}
 */
export function analyzeResult(answers = []) {
  const total = answers.length;
  const correct = answers.filter((a) => a.correct).length;
  const byLevel = summarizeByLevel(answers);
  const level = scoreToLevel(correct, total, byLevel);

  return {
    total,
    correct,
    rate: total > 0 ? Number((correct / total).toFixed(3)) : 0,
    level,
    byLevel,
    weakSpots: findWeakSpots(byLevel),
    pathAdvice: buildPathAdvice(level.courseLevel),
  };
}
